import type { ReactNode } from 'react';

/**
 * Traductions italiennes de l'administration.
 *
 * LA CLÉ EST LA PHRASE FRANÇAISE, à la lettre. Une clé absente d'ici n'est pas
 * une erreur : l'écran affiche alors le français. Voir `i18n.tsx`.
 *
 * Le lexique est le MÊME que celui du simulateur — semirimorchio, ralla,
 * aggancio, stallo, anagrafica, riconciliazione… — et pour la même raison : deux
 * écrans du même système qui nommeraient différemment la même chose coûteraient
 * plus qu'ils ne rapportent. Le glossaire complet, et la réserve qui
 * l'accompagne, sont en tête de `apps/simulator-web/src/traductions-it.tsx`.
 *
 * Les états du moteur — « confirmed », « qr_only », « conflict »… — ne se
 * traduisent pas : ce sont les valeurs que l'API livre.
 */

export const TEXTES: Record<string, string> = {
  Aide: 'Aiuto',
  'Mode d’emploi': 'Manuale d’uso',
  'S’ouvre dans un nouvel onglet': 'Si apre in una nuova scheda',
  'UNICA — Università degli Studi di Cagliari, chef de file':
    'UNICA — Università degli Studi di Cagliari, capofila',
  'EPCIC — Établissement public du commerce et de l’industrie de Corse':
    'EPCIC — Ente pubblico del commercio e dell’industria della Corsica',
  'Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG':
    'Interreg Marittimo Italia-Francia — cofinanziato dall’Unione europea — EASY2LOG',
  // ── Connexion ──────────────────────────────────────────────────────────────
  'Votre mot de passe ne va qu’à Supabase. Les données, elles, sont lues par le serveur avec un rôle dédié — le navigateur ne parle jamais directement à la base.':
    'La password viene inviata solo a Supabase. I dati, invece, sono letti dal server con un ruolo dedicato: il browser non dialoga mai direttamente con la banca dati.',
  Courriel: 'E-mail',
  'Mot de passe': 'Password',
  'Connexion…': 'Accesso in corso…',
  'Se connecter': 'Accedi',
  'Se déconnecter': 'Disconnetti',
  Rafraîchir: 'Aggiorna',

  // ── Onglets ────────────────────────────────────────────────────────────────
  Mouvements: 'Movimenti',
  Prévisionnel: 'Previsionale',
  Anomalies: 'Anomalie',
  Santé: 'Salute',

  // ── Mouvements ─────────────────────────────────────────────────────────────
  'À revoir uniquement': 'Solo da rivedere',
  Référence: 'Riferimento',
  Remorque: 'Semirimorchio',
  Identification: 'Identificazione',
  Position: 'Posizione',
  Place: 'Stallo',
  'Conf.': 'Aff.',
  simulé: 'simulato',
  'Aucun mouvement.': 'Nessun movimento.',
  'Sélectionner un mouvement pour voir sa chronologie.':
    'Selezionare un movimento per vederne la cronologia.',
  'Ouvrir dans une carte': 'Apri in una mappa',

  Confirmée: 'Confermata',
  'QR seul': 'Solo QR',
  'Opérateur seul': 'Solo operatore',
  Conflit: 'Conflitto',
  'Non identifiée': 'Non identificata',

  Tracteur: 'Ralla',
  oui: 'sì',
  non: 'no',
  Opérateur: 'Operatore',
  Motifs: 'Motivi',
  Chronologie: 'Cronologia',

  // ── Correction ─────────────────────────────────────────────────────────────
  'Correction manuelle': 'Correzione manuale',
  'Une correction est une SOURCE, pas une retouche : elle s’ajoute à l’historique et survit au recalcul.':
    'Una correzione è una SORGENTE, non un ritocco: si aggiunge allo storico e sopravvive al ricalcolo.',
  'Immatriculation retenue': 'Targa scelta',
  'Motif (obligatoire) — il sera relu dans six mois':
    'Motivo (obbligatorio) — sarà riletto fra sei mesi',
  'Enregistrer la correction': 'Salva la correzione',

  // ── Chronologie ────────────────────────────────────────────────────────────
  'Aucune observation rattachée à ce mouvement.':
    'Nessuna osservazione collegata a questo movimento.',
  'Lecture QR': 'Lettura QR',
  'Détection d’attelage': 'Rilevamento di aggancio',
  Attelée: 'Agganciato',
  'Détection de dételage': 'Rilevamento di sgancio',
  Dételée: 'Sganciato',
  Dépose: 'Deposito',
  'Signal de vie du boîtier': 'Segnale di vita dell’unità di bordo',
  'Perte du signal GNSS': 'Perdita del segnale GNSS',
  'Retour du signal GNSS': 'Ritorno del segnale GNSS',
  'Entrée en zone de contrôle': 'Ingresso in zona di controllo',
  'Sortie de zone de contrôle': 'Uscita dalla zona di controllo',
  'Saisie opérateur': 'Inserimento operatore',
  'Décision de rapprochement': 'Decisione di riconciliazione',

  // ── Prévisionnel ───────────────────────────────────────────────────────────
  'Ce que le diffuseur voit': 'Ciò che vede il diffusore',
  Annoncées: 'Annunciati',
  Sorties: 'Usciti',
  'Encore attendues': 'Ancora attesi',
  'En retard': 'In ritardo',
  Prochaine: 'Prossimo',
  '{remorque} vers {heure}': '{remorque} verso le {heure}',
  Rang: 'Ordine',
  Disponible: 'Disponibile',
  'en retard': 'in ritardo',
  'Origine de l’heure': 'Origine dell’ora',
  sortie: 'uscito',
  'estimée — cadence du jour': 'stimata — cadenza del giorno',
  'estimée — cadence de référence': 'stimata — cadenza di riferimento',
  'Aucune remorque annoncée pour cette escale. Le prévisionnel vient du plan de chargement : sans manifeste importé, il n’y a rien à annoncer.':
    'Nessun semirimorchio annunciato per questo scalo. Il previsionale deriva dal piano di carico: senza piano importato non c’è nulla da annunciare.',

  // ── Anomalies ──────────────────────────────────────────────────────────────
  Détectée: 'Rilevata',
  Code: 'Codice',
  Sévérité: 'Gravità',
  Mouvement: 'Movimento',
  Détail: 'Dettaglio',
  'Prendre en compte': 'Prendi in carico',
  'Aucune anomalie ouverte.': 'Nessuna anomalia aperta.',

  // ── Santé ──────────────────────────────────────────────────────────────────
  'Files et traitements': 'Code e elaborazioni',
  'Messages non traitables': 'Messaggi non elaborabili',
  'Dernière observation': 'Ultima osservazione',
  'Observations (1 h)': 'Osservazioni (1 h)',
  'Boîtiers vus (1 h)': 'Unità di bordo viste (1 h)',
  'Observations en retard (1 h)': 'Osservazioni in ritardo (1 h)',
  'Un pic signale une coupure réseau en cours, pas une panne : les boîtiers rattrapent leur tampon.':
    'Un picco segnala un’interruzione di rete in corso, non un guasto: le unità di bordo stanno smaltendo il loro buffer.',
  'Dérive d’horloge des boîtiers': 'Deriva dell’orologio delle unità di bordo',
  'Tout le rapprochement temporel repose sur l’horloge du boîtier. Une dérive non détectée rend les fenêtres inopérantes sans que rien ne le signale.':
    'L’intera riconciliazione temporale si basa sull’orologio dell’unità di bordo. Una deriva non rilevata rende le finestre inefficaci senza che nulla lo segnali.',
  Boîtier: 'Unità di bordo',
  Dérive: 'Deriva',
  Mesurée: 'Misurata',
  'Dernier contact': 'Ultimo contatto',

  // ── Position ───────────────────────────────────────────────────────────────
  'position fiable': 'posizione affidabile',
  'position douteuse': 'posizione dubbia',

  // ── Erreurs ────────────────────────────────────────────────────────────────
  'Affichage interrompu': 'Visualizzazione interrotta',
  'Les données sont intactes : seule leur mise en forme a échoué. Recharger la page ; si l’erreur persiste, elle est reproductible et donc corrigeable.':
    'I dati sono intatti: solo la loro formattazione è fallita. Ricaricare la pagina; se l’errore persiste è riproducibile e quindi correggibile.',
  Recharger: 'Ricarica',
  'Erreur inconnue': 'Errore sconosciuto',
  'Connexion refusée.': 'Accesso rifiutato.',
};

/**
 * Blocs à balisage. Le français correspondant est écrit sur place dans le JSX ;
 * ici on donne l'italien sous la même clé. Une clé absente ⇒ le français reste.
 */
export const RICHES: Record<string, ReactNode> = {
  diffuseur: (
    <>
      <strong>Ciò che vede il diffusore</strong> — stesso dato di UNICA, servito dalla stessa vista.
    </>
  ),

  'origine-heure': (
    <>
      L’ora è <em>constatata</em> una volta che il semirimorchio è depositato, e <em>stimata</em>{' '}
      prima — sulla cadenza realmente misurata durante lo sbarco, oppure sulla cadenza di
      riferimento finché nulla è uscito.
    </>
  ),
};
